// ── CompareCanvas.js ──────────────────────────────────────────────────────────
// Race: EP-ADCS vs Standalone Cold Gas — 100 mm station-keeping target.
//
// EP-ADCS:    J_mib → m_emms (1 kg) → Δv = J/m       large velocity per kick
//             Hull steps (m_emms/M)×L_emms per cycle via EMMS momentum exchange
//             Constant displacement/cycle → reaches 100 mm in ~3 cycles
//
// Standalone: J_mib → M_body (8 kg) → Δv = J/M       8× smaller per kick
//             No drag between kicks (space — only retro burns stop it)
//             Bang-bang: accel until braking dist ≥ remaining, then retro to stop

import { useRef, useEffect, useCallback } from "react";
import { useTheme } from "./appHelpers";

const TARGET_M = 0.1;   // m — 100 mm station-keeping box
const G0       = 9.81;
const MAX_DSIM = 60;
const ISP_ION  = 1500;
const MFONT    = "'JetBrains Mono', monospace";
const PH_COL   = ["#ef4444","#fbbf24","#0e9cc0","#22c55e","#a855f7","#f59e0b"];

function rr(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y); ctx.arcTo(x + w, y,     x + w, y + r,     r);
  ctx.lineTo(x + w, y + h - r); ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h); ctx.arcTo(x,     y + h, x,     y + h - r, r);
  ctx.lineTo(x, y + r);     ctx.arcTo(x,     y,     x + r, y,         r);
  ctx.closePath();
}

// Format propellant mass with auto unit
function fmtProp(kg) {
  if (kg <= 0) return "0 pg";
  const pg = kg * 1e12;
  if (pg < 999)  return `${pg.toFixed(1)} pg`;
  const ng = kg * 1e9;
  if (ng < 999)  return `${ng.toFixed(2)} ng`;
  return `${(kg * 1e6).toFixed(3)} μg`;
}

function fmtVel(ms) {
  const mms = ms * 1000;
  if (Math.abs(mms) >= 0.001) return `${mms.toFixed(3)} mm/s`;
  return `${(ms * 1e6).toFixed(1)} μm/s`;
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function CompareCanvas({ mode, params, simRef }) {
  const themeRef   = useRef(null);
  const theme      = useTheme();
  themeRef.current = theme;

  const canvasRef      = useRef(null);
  const cgRef          = useRef(null);
  const epFinishRef    = useRef(null);
  const lastCyclesRef  = useRef(0);
  const rafRef         = useRef(null);
  const prevTAbsRef    = useRef(null);
  const paramsRef      = useRef(params);
  useEffect(() => { paramsRef.current = params; }, [params]);

  const initCG = useCallback(() => ({
    x:              0,
    v:              0,       // m/s — velocity (NO drag in space; only retro burns stop it)
    prop:           0,       // kg — propellant consumed
    accel_kicks:    0,
    retro_kicks:    0,
    phase:          'accel', // 'accel' | 'retro' | 'done'
    done:           false,
    finish_t:       null,
    kick_sim_t:     -999,    // sim-time of last accel kick (for flash visual)
    retro_sim_t:    -999,    // sim-time of last retro kick
  }), []);

  useEffect(() => {
    cgRef.current         = initCG();
    epFinishRef.current   = null;
    lastCyclesRef.current = 0;
    prevTAbsRef.current   = null;
  }, [mode, params, initCG]);

  // ── Physics advance ──────────────────────────────────────────────────────────
  const advance = useCallback(() => {
    const epS = simRef?.current;
    if (!epS) return;

    const tNow = epS.tAbs;
    if (prevTAbsRef.current === null) {
      prevTAbsRef.current   = tNow;
      lastCyclesRef.current = epS.cycles;
      return;
    }

    const dtSim = Math.min(tNow - prevTAbsRef.current, MAX_DSIM);
    prevTAbsRef.current = tNow;

    const p   = paramsRef.current;
    const Mc  = p.m_hull + p.m_emms;
    const Jm  = p.J_mib;
    const Ji  = (Jm / (p.isp * G0)) * ISP_ION * G0;
    const Jc  = mode === 'coldgas' ? Jm : Ji;
    const Ic  = mode === 'coldgas' ? p.isp : ISP_ION;
    const dv  = Jc / Mc;  // Δv per kick on whole body

    // Average cycle time — used for bang-bang braking estimate
    const avg_T = epS.cycles > 1 ? epS.tAbs / epS.cycles : 32.5;

    let nc = { ...cgRef.current };
    if (nc.done) { cgRef.current = nc; return; }

    // ── Coast at constant velocity (NO drag — spacecraft in vacuum) ────────────
    // Between MIB kicks velocity is constant; only retro burns change it.
    if (dtSim > 0) {
      nc.x += nc.v * dtSim;   // x grows linearly at constant v between kicks
    }

    // ── At each new EP-ADCS cycle: fire accel or retro kick ───────────────────
    const newCycles = epS.cycles - lastCyclesRef.current;
    lastCyclesRef.current = epS.cycles;

    if (newCycles > 0) {
      for (let k = 0; k < newCycles; k++) {
        if (nc.phase === 'accel') {
          const remaining = TARGET_M - nc.x;
          // Braking distance: body coasts at decreasing speed over N_stop retro kicks
          // Each retro reduces v by dv; dist per retro coast = avg_T × v_at_that_kick
          // Total = avg_T × (v + (v-dv) + ... + dv) = avg_T × dv × N(N+1)/2
          const N_stop    = Math.ceil(nc.v / Math.max(dv, 1e-15));
          const brake_d   = avg_T * dv * (N_stop * (N_stop + 1)) / 2;

          if (remaining > brake_d * 1.08 && remaining > 0) {
            nc.v          += dv;
            nc.accel_kicks++;
            nc.prop       += Jc / (Ic * G0);
            nc.kick_sim_t  = tNow;
          } else {
            nc.phase = 'retro';
          }
        }

        if (nc.phase === 'retro') {
          nc.v = Math.max(0, nc.v - dv);
          nc.retro_kicks++;
          nc.prop        += Jc / (Ic * G0);
          nc.retro_sim_t  = tNow;
          if (nc.v <= 0) {
            nc.v     = 0;
            nc.phase = 'done';
            nc.done  = true;
            nc.finish_t = tNow;
          }
        }
      }
    }

    if (nc.done && nc.x > TARGET_M) nc.x = TARGET_M;
    cgRef.current = nc;
  }, [mode, simRef]);

  // ── Draw ──────────────────────────────────────────────────────────────────────
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const Cx     = themeRef.current;
    const epS    = simRef?.current;
    const cg     = cgRef.current;
    if (!canvas || !Cx || !epS || !cg) return;

    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    const p   = paramsRef.current;
    const Mc  = p.m_hull + p.m_emms;
    const Jm  = p.J_mib;
    const Ji  = (Jm / (p.isp * G0)) * ISP_ION * G0;
    const Jc  = mode === 'coldgas' ? Jm : Ji;
    const Ic  = mode === 'coldgas' ? p.isp : ISP_ION;
    const cs  = (p.m_emms / Mc) * p.L_emms;

    const compColor = mode === 'coldgas' ? '#22c55e' : '#f59e0b';
    const compName  = mode === 'coldgas' ? 'COLD GAS' : 'ION THRUSTER';

    // Key physics display values
    const dv_ep   = Jm / p.m_emms;   // m/s — mass Δv (EP-ADCS S4)
    const dv_cg   = Jc / Mc;         // m/s — body Δv (standalone)
    const ratio   = Mc / p.m_emms;   // mass ratio
    const pep_cyc = Jm / (p.isp * G0);  // kg prop / EP-ADCS cycle
    const pcg_kck = Jc / (Ic  * G0);    // kg prop / CG kick
    const N_ep    = Math.ceil(TARGET_M / cs);
    const avg_T   = epS.cycles > 1 ? epS.tAbs / epS.cycles : 32.5;

    // EP-ADCS hull position
    const x_ep = epS.xCom - (p.m_emms / Mc) * (epS.xRel - p.L_emms / 2);
    if (!epFinishRef.current && x_ep >= TARGET_M) epFinishRef.current = epS.tAbs;

    // Layout
    const CX_START = W * 0.065;
    const FINISH_X = W * 0.905;
    const vScale   = (FINISH_X - CX_START) / TARGET_M;
    const HULL_W   = Math.min(p.L_hull * 900, 160);
    const HULL_H   = 26;
    const MASS_W   = 10, MASS_H = 18;
    const EMMS_R   = Math.floor(HULL_W / 2 - MASS_W / 2 - 2);
    const ROW1_Y   = H * 0.165;
    const ROW2_Y   = H * 0.665;
    const MID_Y    = H * 0.43;
    const STATS_Y  = H * 0.845;

    // ── Background ─────────────────────────────────────────────────────────────
    ctx.fillStyle = Cx.bg; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = '#0e9cc008'; ctx.fillRect(0, 0, W, H * 0.41);
    ctx.fillStyle = compColor + '06'; ctx.fillRect(0, H * 0.52, W, H * 0.32);
    // Stats bar bg
    ctx.fillStyle = Cx.panel + 'aa'; ctx.fillRect(0, STATS_Y, W, H - STATS_Y);
    ctx.strokeStyle = Cx.border + '88'; ctx.lineWidth = 0.5;
    ctx.beginPath(); ctx.moveTo(0, STATS_Y); ctx.lineTo(W, STATS_Y); ctx.stroke();

    // ── EP-ADCS cycle grid lines ───────────────────────────────────────────────
    ctx.setLineDash([2, 5]);
    let gn = 0;
    for (let d = cs; d < TARGET_M + cs * 0.5; d += cs) {
      gn++;
      const mx = CX_START + d * vScale;
      if (mx > FINISH_X + 2) break;
      ctx.strokeStyle = Cx.border + '30'; ctx.lineWidth = 0.5;
      ctx.beginPath(); ctx.moveTo(mx, 0); ctx.lineTo(mx, STATS_Y); ctx.stroke();
      ctx.font = `7px ${MFONT}`; ctx.fillStyle = Cx.muted + '50'; ctx.textAlign = 'center';
      ctx.fillText(`c${gn}`, mx, 9);
    }
    ctx.setLineDash([]);

    // ── Finish / 100 mm line ───────────────────────────────────────────────────
    ctx.strokeStyle = '#f59e0bcc'; ctx.lineWidth = 2.5; ctx.setLineDash([4, 3]);
    ctx.beginPath(); ctx.moveTo(FINISH_X, 0); ctx.lineTo(FINISH_X, STATS_Y); ctx.stroke();
    ctx.setLineDash([]);
    ctx.font = `bold 10px ${MFONT}`; ctx.fillStyle = '#f59e0b'; ctx.textAlign = 'center';
    ctx.fillText('100 mm', FINISH_X, 10);
    ctx.font = `8px ${MFONT}`; ctx.fillStyle = '#f59e0b88';
    ctx.fillText('STOP', FINISH_X, STATS_Y - 4);

    // ══════════════════════════════════════════════════════════════════════════
    // MIDDLE DATA PANEL
    // ══════════════════════════════════════════════════════════════════════════
    const panH = 82;
    const panY = MID_Y - panH / 2;
    ctx.fillStyle = Cx.panel + 'dd'; ctx.fillRect(0, panY, W, panH);
    ctx.strokeStyle = Cx.border + '60'; ctx.lineWidth = 0.5;
    ctx.beginPath(); ctx.moveTo(0, panY); ctx.lineTo(W, panY); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(0, panY + panH); ctx.lineTo(W, panY + panH); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(W / 2, panY + 6); ctx.lineTo(W / 2, panY + panH - 6); ctx.stroke();

    // ── Left: EP-ADCS ─────────────────────────────────────────────────────────
    {
      const lx = CX_START;
      let ly = panY + 14;
      const ep_prop_total = fmtProp(epS.cycles * pep_cyc);
      const epCycT = avg_T.toFixed(1);

      ctx.textAlign = 'left';
      ctx.font = `bold 10px ${MFONT}`; ctx.fillStyle = '#0e9cc0';
      ctx.fillText('EP-ADCS  (electromagnetic mass shuttle)', lx, ly); ly += 14;

      ctx.font = `9px ${MFONT}`; ctx.fillStyle = Cx.muted;
      ctx.fillText(`Impulse:  J = ${(Jm*1000).toFixed(1)} mN·s   on   ${p.m_emms} kg moving mass`, lx, ly); ly += 12;

      ctx.font = `bold 11px ${MFONT}`; ctx.fillStyle = '#0e9cc0';
      ctx.fillText(`Δv_mass = ${fmtVel(dv_ep)}`, lx, ly);
      ctx.font = `9px ${MFONT}`; ctx.fillStyle = Cx.muted;
      ctx.fillText(`   (${ratio.toFixed(0)}× larger than CG — only ${p.m_emms} kg moves)`, lx + 130, ly); ly += 13;

      ctx.font = `9px ${MFONT}`; ctx.fillStyle = Cx.text;
      ctx.fillText(`Step/cycle: ${(cs*1000).toFixed(3)} mm  ·  cycle ≈ ${epCycT} s  ·  to 100 mm: ~${N_ep} cycles`, lx, ly); ly += 12;

      ctx.font = `bold 10px ${MFONT}`; ctx.fillStyle = '#22c55e';
      ctx.fillText(`PROP:  ${fmtProp(pep_cyc)} / cycle  ·  total: ${ep_prop_total}  [${epS.cycles} cycles done]`, lx, ly);
    }

    // ── Right: Standalone CG ──────────────────────────────────────────────────
    {
      const rx = W / 2 + 10;
      let ry = panY + 14;
      const totalKicks = cg.accel_kicks + cg.retro_kicks;

      ctx.textAlign = 'left';
      ctx.font = `bold 10px ${MFONT}`; ctx.fillStyle = compColor;
      ctx.fillText(`${compName}  (direct thrust on full body)`, rx, ry); ry += 14;

      ctx.font = `9px ${MFONT}`; ctx.fillStyle = Cx.muted;
      ctx.fillText(`Impulse:  J = ${(Jc*1000).toFixed(1)} mN·s   on   ${Mc.toFixed(1)} kg full body`, rx, ry); ry += 12;

      ctx.font = `bold 11px ${MFONT}`; ctx.fillStyle = compColor;
      ctx.fillText(`Δv/kick = ${fmtVel(dv_cg)}`, rx, ry);
      ctx.font = `9px ${MFONT}`; ctx.fillStyle = Cx.muted;
      ctx.fillText(`   (${ratio.toFixed(0)}× smaller — pushing ${Mc.toFixed(0)} kg)`, rx + 130, ry); ry += 13;

      ctx.font = `9px ${MFONT}`; ctx.fillStyle = Cx.text;
      const phLabel = cg.phase === 'accel' ? `ACCEL ↑${cg.accel_kicks}` : cg.phase === 'retro' ? `RETRO ↓${cg.retro_kicks}` : 'DONE';
      ctx.fillText(`Phase: ${phLabel}  ·  v = ${fmtVel(cg.v)}  ·  pos = ${(cg.x*1000).toFixed(2)} mm`, rx, ry); ry += 12;

      ctx.font = `bold 10px ${MFONT}`; ctx.fillStyle = '#22c55e';
      ctx.fillText(`PROP:  ${fmtProp(pcg_kck)} / kick  ·  total: ${fmtProp(cg.prop)}  [${totalKicks} kicks done]`, rx, ry);
    }

    // ══════════════════════════════════════════════════════════════════════════
    // ROW 1 — EP-ADCS
    // ══════════════════════════════════════════════════════════════════════════
    const ep_px = Math.min(CX_START + x_ep * vScale, FINISH_X + HULL_W / 2 - 2);

    // Hull
    rr(ctx, ep_px - HULL_W / 2, ROW1_Y - HULL_H / 2, HULL_W, HULL_H, 5);
    ctx.fillStyle = '#0e9cc015'; ctx.fill();
    ctx.strokeStyle = '#0e9cc0'; ctx.lineWidth = 1.5; ctx.stroke();

    // Moving mass
    const massCX = Math.min(Math.max(
      ep_px + epS.xRel * (EMMS_R / (p.L_emms / 2)),
      ep_px - EMMS_R), ep_px + EMMS_R);
    const phCol = PH_COL[epS.phase] ?? '#a855f7';
    rr(ctx, massCX - MASS_W / 2, ROW1_Y - MASS_H / 2, MASS_W, MASS_H, 2);
    ctx.fillStyle = phCol + '30'; ctx.fill();
    ctx.strokeStyle = phCol; ctx.lineWidth = 1.5; ctx.stroke();
    ctx.font = `7px ${MFONT}`; ctx.fillStyle = phCol + 'cc'; ctx.textAlign = 'center';
    ctx.fillText(`${p.m_emms}kg`, massCX, ROW1_Y + 3);

    // S4 MIB exhaust on mass
    if (epS.phase === 4) {
      for (let i = 0; i < 6; i++) {
        const phi = ((epS.tAbs * 20 + i * 0.67) % 1);
        const d = 4 + phi * 18;
        ctx.beginPath();
        ctx.arc(massCX - MASS_W / 2 - d, ROW1_Y + Math.sin(epS.tAbs * 25 + i * 1.7) * 2.5, 1.3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(168,85,247,${((1 - phi) * 0.85).toFixed(2)})`; ctx.fill();
      }
      ctx.font = `8px ${MFONT}`; ctx.fillStyle = '#a855f7'; ctx.textAlign = 'center';
      ctx.fillText(
        `J=${(Jm*1000).toFixed(1)}mNs → ${p.m_emms}kg  +${fmtVel(dv_ep)}  [CG equiv: +${fmtVel(dv_cg)} on ${Mc}kg]`,
        massCX, ROW1_Y - MASS_H / 2 - 4);
    }

    // EP-ADCS labels
    ctx.font = `bold 10px ${MFONT}`; ctx.fillStyle = '#0e9cc0'; ctx.textAlign = 'left';
    ctx.fillText(
      `EP-ADCS  ·  ${(cs*1000).toFixed(3)} mm / cycle  ·  ${p.m_hull}kg hull  +  ${p.m_emms}kg mass`,
      CX_START, ROW1_Y - HULL_H / 2 - 12);
    ctx.font = `9px ${MFONT}`; ctx.fillStyle = Cx.muted;
    ctx.fillText(
      `Δv_mass=${fmtVel(dv_ep)} on ${p.m_emms}kg  ·  prop: ${fmtProp(epS.cycles * pep_cyc)}  ·  ${epS.cycles} cycles`,
      CX_START, ROW1_Y - HULL_H / 2 - 2);

    ctx.font = `10px ${MFONT}`; ctx.fillStyle = '#0e9cc0'; ctx.textAlign = 'center';
    ctx.fillText(
      `Δ = ${(x_ep * 1000).toFixed(2)} mm  [cycle ${epS.cycles}]`,
      Math.min(ep_px, FINISH_X - 10), ROW1_Y + HULL_H / 2 + 13);

    if (epFinishRef.current) {
      ctx.font = `bold 10px ${MFONT}`; ctx.fillStyle = '#22c55e'; ctx.textAlign = 'left';
      ctx.fillText(`✓ 100mm  t = ${epFinishRef.current.toFixed(0)} s`, FINISH_X + 4, ROW1_Y + 4);
    }

    // ══════════════════════════════════════════════════════════════════════════
    // ROW 2 — Standalone (bang-bang, no drag)
    // ══════════════════════════════════════════════════════════════════════════
    const cg_px  = Math.min(CX_START + cg.x * vScale, FINISH_X + HULL_W / 2 - 2);

    // Hull
    rr(ctx, cg_px - HULL_W / 2, ROW2_Y - HULL_H / 2, HULL_W, HULL_H, 5);
    ctx.fillStyle = compColor + '12'; ctx.fill();
    ctx.strokeStyle = compColor; ctx.lineWidth = 1.5; ctx.stroke();

    // Labels inside hull
    const phCol2 = cg.phase === 'accel' ? compColor : cg.phase === 'retro' ? '#ef4444' : '#22c55e';
    ctx.font = `bold 7px ${MFONT}`; ctx.fillStyle = phCol2; ctx.textAlign = 'center';
    ctx.fillText(cg.phase.toUpperCase(), cg_px, ROW2_Y - 4);
    ctx.font = `7px ${MFONT}`; ctx.fillStyle = compColor + 'aa';
    ctx.fillText(`${Mc.toFixed(0)} kg`, cg_px, ROW2_Y + 7);

    // ── Flash window for kick visual (independent of EP-ADCS phase) ────────────
    const flashDur = Math.max(2.5, (p.J_mib / p.F_reset) * 25);
    const fwdOn    = cg.phase === 'accel' && (epS.tAbs - cg.kick_sim_t)  < flashDur;
    const retOn    = cg.phase === 'retro' && (epS.tAbs - cg.retro_sim_t) < flashDur;

    // ── Forward nozzle (LEFT face → accelerates right) ─────────────────────────
    {
      const nx = cg_px - HULL_W / 2;
      const glowR = fwdOn ? 22 : (cg.phase === 'accel' && cg.v > 1e-9 ? 6 : 0);
      if (glowR > 0) {
        const grd = ctx.createRadialGradient(nx - 4, ROW2_Y, 1, nx - 8, ROW2_Y, glowR);
        grd.addColorStop(0, compColor + (fwdOn ? 'cc' : '28'));
        grd.addColorStop(1, compColor + '00');
        ctx.fillStyle = grd;
        ctx.beginPath(); ctx.arc(nx - 8, ROW2_Y, glowR, 0, Math.PI * 2); ctx.fill();
      }
      if (cg.phase === 'accel') {
        ctx.fillStyle = compColor + (fwdOn ? '80' : '22');
        ctx.beginPath();
        ctx.moveTo(nx,      ROW2_Y - (fwdOn ? 8 : 4));
        ctx.lineTo(nx - (fwdOn ? 32 : 10), ROW2_Y);
        ctx.lineTo(nx,      ROW2_Y + (fwdOn ? 8 : 4));
        ctx.fill();

        // Particles — always on while accel (slow drift), bright on kick
        const nP = fwdOn ? 12 : 5;
        const spd = fwdOn ? 16 : 2.5;  // slow clock = slow visible drift
        for (let i = 0; i < nP; i++) {
          const phi  = ((epS.tAbs * spd + i / nP) % 1);
          const dist = 6 + phi * (fwdOn ? 50 : 25);
          const spr  = Math.sin(epS.tAbs * 7 + i * 1.8) * (fwdOn ? 4 : 2);
          const alph = (1 - phi) * (fwdOn ? 0.9 : 0.18);
          const rgb  = mode === 'coldgas' ? '34,197,94' : '245,158,11';
          ctx.beginPath();
          ctx.arc(nx - dist, ROW2_Y + spr, Math.max(0.3, 1.8 * (1 - phi * 0.5)), 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${rgb},${alph.toFixed(2)})`; ctx.fill();
        }
        if (fwdOn) {
          ctx.font = `8px ${MFONT}`; ctx.fillStyle = compColor; ctx.textAlign = 'center';
          ctx.fillText(
            `J=${(Jc*1000).toFixed(1)}mNs → ${Mc.toFixed(0)}kg  +${fmtVel(dv_cg)}`,
            nx - 24, ROW2_Y - HULL_H / 2 - 4);
        }
      }
    }

    // ── Retro nozzle (RIGHT face → fires right, decelerates) ──────────────────
    {
      const rx2 = cg_px + HULL_W / 2;
      if (cg.phase === 'retro' || cg.phase === 'done') {
        const glowR = retOn ? 22 : 6;
        const grd2 = ctx.createRadialGradient(rx2 + 4, ROW2_Y, 1, rx2 + 8, ROW2_Y, glowR);
        grd2.addColorStop(0, '#ef4444' + (retOn ? 'cc' : '28'));
        grd2.addColorStop(1, '#ef444400');
        ctx.fillStyle = grd2;
        ctx.beginPath(); ctx.arc(rx2 + 8, ROW2_Y, glowR, 0, Math.PI * 2); ctx.fill();

        ctx.fillStyle = '#ef4444' + (retOn ? '80' : '25');
        ctx.beginPath();
        ctx.moveTo(rx2,      ROW2_Y - (retOn ? 8 : 5));
        ctx.lineTo(rx2 + (retOn ? 32 : 12), ROW2_Y);
        ctx.lineTo(rx2,      ROW2_Y + (retOn ? 8 : 5));
        ctx.fill();

        // Retro particles
        const nP = retOn ? 12 : 5;
        const spd = retOn ? 16 : 2.5;
        for (let i = 0; i < nP; i++) {
          const phi  = ((epS.tAbs * spd + i / nP) % 1);
          const dist = 6 + phi * (retOn ? 50 : 25);
          const spr  = Math.sin(epS.tAbs * 7 + i * 1.8) * (retOn ? 4 : 2);
          const alph = (1 - phi) * (retOn ? 0.9 : 0.18);
          ctx.beginPath();
          ctx.arc(rx2 + dist, ROW2_Y + spr, Math.max(0.3, 1.8 * (1 - phi * 0.5)), 0, Math.PI * 2);
          ctx.fillStyle = `rgba(239,68,68,${alph.toFixed(2)})`; ctx.fill();
        }
        if (retOn) {
          ctx.font = `8px ${MFONT}`; ctx.fillStyle = '#ef4444'; ctx.textAlign = 'center';
          ctx.fillText(
            `RETRO  J=${(Jc*1000).toFixed(1)}mNs → -${fmtVel(dv_cg)}`,
            rx2 + 24, ROW2_Y - HULL_H / 2 - 4);
        }
      }
    }

    // Velocity arrow above hull
    if (cg.v > 1e-10) {
      const maxV = dv_cg * 20;
      const vPx  = Math.max(5, Math.min(75, (cg.v / maxV) * 75));
      const arY  = ROW2_Y - HULL_H / 2 - 7;
      ctx.strokeStyle = compColor + 'bb'; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(cg_px - vPx / 2, arY); ctx.lineTo(cg_px + vPx / 2, arY); ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cg_px + vPx / 2, arY);
      ctx.lineTo(cg_px + vPx / 2 - 5, arY - 3);
      ctx.lineTo(cg_px + vPx / 2 - 5, arY + 3);
      ctx.fillStyle = compColor; ctx.fill();
      ctx.font = `8px ${MFONT}`; ctx.fillStyle = compColor + 'cc'; ctx.textAlign = 'center';
      ctx.fillText(`v = ${fmtVel(cg.v)}`, cg_px, arY - 5);
    }

    // Standalone labels
    ctx.font = `bold 10px ${MFONT}`; ctx.fillStyle = compColor; ctx.textAlign = 'left';
    ctx.fillText(
      `${compName} STANDALONE  ·  bang-bang to 100 mm  ·  pushing ${Mc.toFixed(0)} kg`,
      CX_START, ROW2_Y - HULL_H / 2 - 20);
    ctx.font = `9px ${MFONT}`; ctx.fillStyle = Cx.muted;
    ctx.fillText(
      `Δv/kick=${fmtVel(dv_cg)} on ${Mc.toFixed(0)} kg  (${ratio.toFixed(0)}× less than EP-ADCS mass)  ·  prop: ${fmtProp(cg.prop)}`,
      CX_START, ROW2_Y - HULL_H / 2 - 9);

    ctx.font = `10px ${MFONT}`; ctx.fillStyle = compColor; ctx.textAlign = 'center';
    ctx.fillText(
      `Δ = ${(cg.x * 1000).toFixed(2)} mm`,
      Math.min(cg_px, FINISH_X - 10), ROW2_Y + HULL_H / 2 + 13);

    if (cg.done) {
      ctx.font = `bold 10px ${MFONT}`; ctx.fillStyle = '#22c55e'; ctx.textAlign = 'left';
      ctx.fillText(`✓ STOPPED at 100 mm  t = ${cg.finish_t.toFixed(0)} s`, FINISH_X + 4, ROW2_Y + 4);
    }

    // ══════════════════════════════════════════════════════════════════════════
    // STATS BAR (bottom)
    // ══════════════════════════════════════════════════════════════════════════
    const epDone   = !!epFinishRef.current;
    const bothDone = epDone && cg.done;
    const totalKicks = cg.accel_kicks + cg.retro_kicks;

    // Left: EP-ADCS prop summary
    const ep_prop_total = epS.cycles * pep_cyc;
    let sx = CX_START;
    const sy = STATS_Y + 12;

    ctx.textAlign = 'left';
    ctx.font = `bold 9px ${MFONT}`; ctx.fillStyle = '#0e9cc0';
    ctx.fillText('EP-ADCS  PROPELLANT:', sx, sy);
    ctx.font = `11px ${MFONT}`; ctx.fillStyle = '#22c55e';
    ctx.fillText(`${fmtProp(ep_prop_total)}`, sx + 145, sy);
    ctx.font = `9px ${MFONT}`; ctx.fillStyle = Cx.muted;
    ctx.fillText(`  =  ${epS.cycles} cyc × ${fmtProp(pep_cyc)}/cyc`, sx + 210, sy);

    // Centre divider
    ctx.strokeStyle = Cx.border + '50'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(W / 2, STATS_Y + 4); ctx.lineTo(W / 2, H - 4); ctx.stroke();

    // Right: CG prop summary
    const rx3 = W / 2 + 10;
    ctx.textAlign = 'left';
    ctx.font = `bold 9px ${MFONT}`; ctx.fillStyle = compColor;
    ctx.fillText(`${compName}  PROPELLANT:`, rx3, sy);
    ctx.font = `11px ${MFONT}`; ctx.fillStyle = '#22c55e';
    ctx.fillText(`${fmtProp(cg.prop)}`, rx3 + 145, sy);
    ctx.font = `9px ${MFONT}`; ctx.fillStyle = Cx.muted;
    ctx.fillText(`  =  ${totalKicks} kicks × ${fmtProp(pcg_kck)}/kick`, rx3 + 210, sy);

    // Bottom line: race status / result
    const sy2 = STATS_Y + 26;
    if (bothDone) {
      const speedup = (cg.finish_t / epFinishRef.current).toFixed(1);
      const propRatio = (cg.prop / ep_prop_total).toFixed(1);
      ctx.font = `bold 9px ${MFONT}`; ctx.fillStyle = '#f59e0b'; ctx.textAlign = 'center';
      ctx.fillText(
        `EP-ADCS wins  ·  ${speedup}× faster  ·  EP: ${epFinishRef.current.toFixed(0)}s / ${epS.cycles}cyc  ·  CG: ${cg.finish_t.toFixed(0)}s / ${totalKicks}kicks  ·  CG used ${propRatio}× more prop`,
        W / 2, sy2);
    } else {
      const epPct = Math.min(100, (x_ep / TARGET_M * 100)).toFixed(0);
      const cgPct = Math.min(100, (cg.x / TARGET_M * 100)).toFixed(0);
      const ahead = x_ep >= cg.x;
      ctx.font = `9px ${MFONT}`; ctx.textAlign = 'left'; ctx.fillStyle = Cx.muted;
      ctx.fillText(
        `EP: ${epPct}% done [${(cs*1000).toFixed(2)}mm/cyc]   CG ${cg.phase}: ${cgPct}% [${fmtVel(dv_cg)}/kick, ${ratio.toFixed(0)}× mass → ${ratio.toFixed(0)}× fewer Δv]`,
        CX_START, sy2);
      ctx.textAlign = 'right'; ctx.fillStyle = ahead ? '#0e9cc0' : compColor;
      ctx.fillText(ahead ? 'EP-ADCS ahead ▶' : `${compName} ahead ▶`, W - 6, sy2);
    }
  }, [mode, simRef]);

  // ── RAF loop ──────────────────────────────────────────────────────────────────
  const loop = useCallback(() => {
    advance();
    draw();
    rafRef.current = requestAnimationFrame(loop);
  }, [advance, draw]);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [loop]);

  useEffect(() => { draw(); }, [draw]);

  return (
    <canvas
      ref={canvasRef}
      width={820} height={350}
      style={{ width: '100%', height: 'auto', display: 'block' }}
    />
  );
}
